import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { Reveal } from "@/components/Reveal";

export const FILTER_OPTIONS = {
  type: ["Residential", "Commercial", "Investment"],
  location: ["Samruddhi Mahamarg", "Umred Road", "Wardha Road", "Nagpur", "Other Locations"],
  size: ["Small", "Medium", "Large"],
  budget: [
    { label: "\u20B920\u201330 Lakhs", value: "20-30" },
    { label: "\u20B925\u201335 Lakhs", value: "25-35" },
    { label: "\u20B935\u201345 Lakhs", value: "35-45" },
    { label: "Above \u20B950 Lakhs", value: "50-plus" },
  ],
};

const selectClass =
  "w-full rounded-sm border border-gray-200 bg-white px-4 py-3.5 text-sm text-navy outline-none transition-colors focus:border-gold focus:ring-2 focus:ring-gold/30";

export const FilterSelect = ({ label, value, onChange, children, testId }) => (
  <label className="block">
    <span className="mb-2 block text-[11px] font-bold uppercase tracking-[0.18em] text-navy/60">{label}</span>
    <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className={selectClass}>
      {children}
    </select>
  </label>
);

export default function SearchFilter() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ type: "", location: "", size: "", budget: "" });

  const set = (key) => (value) => setFilters((f) => ({ ...f, [key]: value }));

  const search = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
    navigate(`/properties${params.toString() ? `?${params}` : ""}`);
  };

  return (
    <section id="search" data-testid="property-search-section" className="relative z-20 -mt-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-[0_20px_60px_rgb(10,25,47,0.12)] sm:p-8">
            <h2 className="font-display text-2xl font-semibold text-navy sm:text-3xl">
              Find the Right Plot for Your Future
            </h2>
            <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              <FilterSelect label="Property Type" value={filters.type} onChange={set("type")} testId="filter-property-type">
                <option value="">Any Type</option>
                {FILTER_OPTIONS.type.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </FilterSelect>
              <FilterSelect label="Location" value={filters.location} onChange={set("location")} testId="filter-location">
                <option value="">Any Location</option>
                {FILTER_OPTIONS.location.map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </FilterSelect>
              <FilterSelect label="Plot Size" value={filters.size} onChange={set("size")} testId="filter-plot-size">
                <option value="">Any Size</option>
                {FILTER_OPTIONS.size.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </FilterSelect>
              <FilterSelect label="Budget" value={filters.budget} onChange={set("budget")} testId="filter-budget">
                <option value="">Any Budget</option>
                {FILTER_OPTIONS.budget.map((b) => (
                  <option key={b.value} value={b.value}>{b.label}</option>
                ))}
              </FilterSelect>
            </div>
            <button
              data-testid="search-properties-button"
              onClick={search}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-sm bg-navy px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:bg-gold hover:text-navy sm:w-auto"
            >
              <Search className="h-4 w-4" aria-hidden="true" />
              Search Properties
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
